import { WorkflowLibraryBody } from "../../app/modules/types/workflowLibrary"
import { apiClient } from "../client"
import { resolveComponentIcon } from "../componentIcons"
import { formatEstimatedScanTime } from "./normalizeLibrary"

// Adapter: orch library DTO → FE WorkflowLibraryItem shape.
// Library node positions arrive flat (`position_x`, `position_y`) and use
// `component_id`. FE keeps `position: {x, y}` + `node_type_id`.
const orchLibraryNodeToFe = (n: any) => ({
    id: n.id,
    position: { x: n.position_x ?? n.position?.x ?? 0, y: n.position_y ?? n.position?.y ?? 0 },
    config: n.config ?? null,
    abstract_config: n.abstract_config ?? null,
    config_schema: n.config_schema ?? null,
    // The orch sends the component *name* here, not a URL; WorkflowNode renders
    // `<img src={icon}>`, so it has to be resolved against the bundled icon set
    // exactly like workflow.api.ts does for the editor canvas.
    icon: resolveComponentIcon(n.icon),
    node_type_id: n.component_id ?? n.node_type_id ?? '',
    ai_assisted: !!n.ai_assisted,
    ai_example: n.ai_example ?? null,
    ai_example_fa: n.ai_example_fa ?? null,
    // WorkflowNode reads `data.ports.target` / `data.ports.source`; the fallback
    // has to use those keys too or a node without component data renders with
    // no handles for its connectors to anchor to.
    data: n.data ?? {
        label: '',
        state: null,
        label_fa: null,
        details: '',
        details_fa: null,
        long_description: '',
        long_description_fa: null,
        ports: { target: [], source: [] },
    },
})

const orchLibraryConnectorToFe = (c: any) => ({
    id: c.id,
    source: c.source ?? c.source_id,
    target: c.target ?? c.target_id,
    sourceHandle: c.source_handle_id ?? c.source_handle ?? c.sourceHandle ?? '',
    targetHandle: c.target_handle_id ?? c.target_handle ?? c.targetHandle ?? '',
})

// The edit form is seeded from a list/detail row, where `category` is a nested
// OBJECT and `tags` an array of objects. The pickers coerce those to ids for
// display but only write a bare id back when the user actually touches them —
// so any control left untouched submits the original object. The orch wants
// ids, so accept both shapes here rather than depending on what was touched.
const toId = (value: any): string | null => {
    if (value == null || value === '') return null
    return typeof value === 'string' ? value : (value.id ?? null)
}

const toIds = (value: any): string[] =>
    Array.isArray(value) ? value.map(toId).filter((id): id is string => !!id) : []

// Adapter: FE WorkflowLibraryBody → orch Create/UpdateLibraryWorkflowDto.
// Reads return `category` (nested object) and `library_tags`, but WRITES take
// `category_id` / `tag_ids`. The orch's ValidationPipe has `whitelist: true`
// (and no `forbidNonWhitelisted`), so an un-mapped `category` / `tags` is
// silently stripped — the request returns 200 with the value unchanged. Map
// here so the read/write asymmetry lives in one place next to the response
// adapter, instead of leaking backend DTO names into the form layer.
const feLibraryBodyToOrch = ({ category, tags, ...rest }: any) => ({
    ...rest,
    ...(category !== undefined ? { category_id: toId(category) } : {}),
    ...(tags !== undefined ? { tag_ids: toIds(tags) } : {}),
})

const orchLibraryDetailToFe = (item: any) => ({
    ...item,
    estimated_scan_time: formatEstimatedScanTime(item.estimated_scan_time),
    graph: item.graph
        ? {
            nodes: (item.graph.nodes ?? []).map(orchLibraryNodeToFe),
            connectors: (item.graph.connectors ?? []).map(orchLibraryConnectorToFe),
        }
        : item.nodes
            ? {
                nodes: (item.nodes ?? []).map(orchLibraryNodeToFe),
                connectors: (item.connectors ?? []).map(orchLibraryConnectorToFe),
            }
            : undefined,
})

export const workflowLibraryApi = {
    getList: async (page: number, search = "", params?: string): Promise<any> => {
        const url = params ? `/workflow/library/?${params}` : '/workflow/library/'
        const { data } = await apiClient.get(url, {
            params: { page_size: 10, page, ...(search ? { search } : {}) },
        })
        if (Array.isArray(data?.results)) {
            data.results = data.results.map((it: any) => ({
                ...it,
                estimated_scan_time: formatEstimatedScanTime(it.estimated_scan_time),
            }))
        }
        return data
    },
    create: async (body: WorkflowLibraryBody): Promise<any> => {
        const { data } = await apiClient.post('/workflow/library/', feLibraryBodyToOrch(body))
        return orchLibraryDetailToFe(data)
    },
    update: async (id: string, body: WorkflowLibraryBody): Promise<any> => {
        const { data } = await apiClient.patch(`/workflow/library/${id}/`, feLibraryBodyToOrch(body))
        return orchLibraryDetailToFe(data)
    },
    getCategory: async (): Promise<any> => {
        const { data } = await apiClient.get("/workflow/library/categories/")
        return Array.isArray(data) ? data : (data?.data ?? [])
    },
    getTag: async (): Promise<any> => {
        const { data } = await apiClient.get("/workflow/library/tags/")
        return Array.isArray(data) ? data : (data?.data ?? [])
    },
    getByID: async (id: string): Promise<any> => {
        const { data } = await apiClient.get(`/workflow/library/${id}/`)
        return orchLibraryDetailToFe(data)
    },
    clone: async (id: string, payload: { name: string }): Promise<any> => {
        const { data } = await apiClient.post(`/workflow/library/${id}/clone/`, payload)
        return orchLibraryDetailToFe(data)
    },
    deleteLibrary: async (id: string): Promise<any> => {
        const { data } = await apiClient.delete(`/workflow/library/${id}/`)
        return data
    },
    updateCategory: async (id: string, payload: {
        name: string,
        display_name: string,
        description: string,
        icon: string
    }): Promise<any> => {
        const { data } = await apiClient.patch(`/workflow/library/categories/${id}/`, payload)
        return data
    },
    createCategory: async (payload: {
        name: string,
        display_name: string,
        description: string,
        icon: string
    }): Promise<any> => {
        const { data } = await apiClient.post(`/workflow/library/categories/`, payload)
        return data
    },
    deleteCategory: async (id: string): Promise<any> => {
        const { data } = await apiClient.delete(`/workflow/library/categories/${id}/`)
        return data
    },
    deleteTag: async (id: string): Promise<any> => {
        const { data } = await apiClient.delete(`/workflow/library/tags/${id}/`)
        return data
    },
    updateTag: async (id: string, payload: { name: string }): Promise<any> => {
        const { data } = await apiClient.patch(`/workflow/library/tags/${id}/`, payload)
        return data
    },
    createTag: async (payload: { name: string }): Promise<any> => {
        const { data } = await apiClient.post(`/workflow/library/tags/`, payload)
        return data
    },
}
